import { isEmpty } from 'lodash';

import { MODE_AIR, MODE_RAIL, MODE_ROAD, MODE_WATER } from '@/lib/constants';
import { OptionModel } from '@/lib/models/option/types';
import { SegmentModeType } from '@/types/api-types';

export const getDirectFlights = (options: OptionModel[]) => {
  return options.filter((option) => option.segments.length === 1);
};

export const getConnectingFlights = (options: OptionModel[]) => {
  return options.filter((option) => option.segments.length > 1 && option.carrierIds.length);
};

export const getNonConnectingFlights = (options: OptionModel[]) => {
  return options.filter((option) => option.segments.length > 1 && !option.carrierIds.length);
};

export const getDirectServices = (options: OptionModel[]) => {
  return options.filter((option) => !option.numTransfers);
};

export const getConnectingShips = (options: OptionModel[]) => {
  return options.filter((option) => option.numTransfers > 0 && option.carrierIds.length);
};

export const getNonConnectingShips = (options: OptionModel[]) => {
  return options.filter((option) => option.numTransfers > 0 && !option.carrierIds.length);
};

export const getAllConnectingServices = (options: OptionModel[]) => {
  return options.filter((option) => option.numTransfers > 0);
};

export interface OptionGroupInterface {
  id: string;
  title: string;
  subTitle?: string;
  options: OptionModel[];
  hasOptions: boolean;
}

export const getGroupedOptions = (options: OptionModel[], mode: SegmentModeType): OptionGroupInterface[] => {
  const groups = [];

  // Air
  if (mode === MODE_AIR) {
    // Pick appropriate options
    const direct = getDirectFlights(options);
    const connecting = getConnectingFlights(options);
    const nonConnecting = getNonConnectingFlights(options);

    // Push direct options to array
    groups.push({
      id: 'direct',
      title: 'Direct flights',
      options: direct,
      hasOptions: !isEmpty(direct),
    });
    // Push connecting options to array
    groups.push({
      id: 'connecting',
      title: 'Connecting flights',
      subTitle: 'With a single carrier',
      options: connecting,
      hasOptions: !isEmpty(connecting),
    });
    // Push nonConnecting options to array
    groups.push({
      id: 'nonconnecting',
      title: 'Non-connecting flights',
      subTitle: 'With different carriers',
      options: nonConnecting,
      hasOptions: !isEmpty(nonConnecting),
    });
  }

  // Ocean
  if (mode === MODE_WATER) {
    // Pick appropriate options
    const direct = getDirectServices(options);
    const connecting = getConnectingShips(options);
    const nonConnecting = getNonConnectingShips(options);

    // Push direct options to array
    groups.push({
      id: 'direct',
      title: 'Direct services',
      options: direct,
      hasOptions: !isEmpty(direct),
    });
    // Push connecting options to array
    groups.push({
      id: 'connecting',
      title: 'Transshipment',
      subTitle: 'With a single carrier',
      options: connecting,
      hasOptions: !isEmpty(connecting),
    });
    // Push nonConnecting options to array
    groups.push({
      id: 'nonconnecting',
      title: 'Transshipment',
      subTitle: 'With different carriers',
      options: nonConnecting,
      hasOptions: !isEmpty(nonConnecting),
    });
  }

  if (mode === MODE_ROAD) {
    groups.push({
      id: 'onDemand',
      title: 'On demand services',
      options,
      hasOptions: !isEmpty(options),
    });
  }

  if (mode === MODE_RAIL) {
    // Pick appropriate options
    const direct = getDirectServices(options);
    const connecting = getAllConnectingServices(options);
    // Push direct options to array
    groups.push({
      id: 'direct',
      title: 'Direct services',
      options: direct,
      hasOptions: !isEmpty(direct),
    });
    // Push connecting options to array
    groups.push({
      id: 'connecting',
      title: 'Transshipment',
      subTitle: 'With a single carrier',
      options: connecting,
      hasOptions: !isEmpty(connecting),
    });
  }

  return groups;
};
